import { ChangeDetectorRef, Component, ElementRef, Injectable, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormGroup, FormRecord, Validators } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as fa from '@fortawesome/free-solid-svg-icons';
import { Assignment } from 'src/app/classes/assignment';
import { Customer } from 'src/app/classes/customer';
import { TranslateFieldPipe } from 'src/app/pipes/translate-field.pipe';
import { SettingsService } from 'src/app/services/settings.service';
import productUnits from 'src/app/constants/product-units';
import { Product } from 'src/app/classes/product';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProductComponent } from '../services/product/product.component';
import { LocationComponent } from './location/location.component';
import { CustomerService } from 'src/app/services/customer.service';
import { ShiftService } from 'src/app/services/shift.service';
import { EmployeeAvailable } from 'src/app/classes/employee-available';
import { Skill } from 'src/app/classes/skill';
import { Badge } from 'src/app/classes/badge';
import { Holiday } from 'src/app/classes/holiday';
import { NgbDateAdapter, NgbDateParserFormatter, NgbDatepicker, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Organization } from 'src/app/classes/organization';
import languages from 'src/app/constants/languages';
import languagesLocalized from 'src/app/constants/languages-localized';
import { AuthService } from 'src/app/services/auth.service';
import { CustomerContact } from 'src/app/classes/customer-contact';
import discountTypes from 'src/app/constants/discount-types';
import { Employee } from 'src/app/classes/employee';
import { FeedbackComponent } from 'src/app/components/shifts/edit/feedback/feedback.component';
import { Title } from '@angular/platform-browser';

@Injectable()
export class CustomDateAdapter {
  fromModel(value: string): NgbDateStruct {
    if (!value)
      //@ts-ignore
      return null

    let parts = value.split('-');
    return { year: +parts[0], month: +parts[1], day: +parts[2] }
  }

  toModel(date: NgbDateStruct): string // from internal model -> your mode
  {
    //@ts-ignore
    return date ? date.year + "-" + ('0' + date.month).slice(-2) + "-" + ('0' + date.day).slice(-2) : null
  }
}

@Injectable()
export class CustomDateParserFormatter {
  parse(value: string): NgbDateStruct | null {
    if (!value) return null;
    const parts = value.trim().split('/');
    if (parts.length === 3) {
      const day = +parts[0];
      const month = +parts[1];
      const year = +parts[2];
      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
        return { year, month, day };
      }
    }
    return null; // invalid format
  }

  format(date: NgbDateStruct): string {
    // @ts-ignore
    return date ? ('0' + date.day).slice(-2) + "/" + ('0' + date.month).slice(-2) + "/" + date.year : null
  }
}

@Component({
  selector: 'app-schedule',
  templateUrl: './schedule.component.html',
  styleUrls: ['./schedule.component.scss'],
  providers: [
    TranslateFieldPipe,
    { provide: NgbDateAdapter, useClass: CustomDateAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }
  ]
})

export class ScheduleComponent implements OnInit {
  @ViewChild('languageSelect') languageSelect!: ElementRef

  assignment = new Assignment()
  badge = ''
  badges: Badge[] = []
  currentUser = new CustomerContact()
  discountTypes = discountTypes
  employees: EmployeeAvailable[] = []
  fa = fa
  firstSearchDone = false
  holidays: Holiday[] = []
  isSignedIn = false
  language = $localize.locale?.split('-')[0]
  languages = languages
  languagesLocalized = languagesLocalized
  localeLang = $localize.locale?.split('-')[0]
  locationName = $localize`Set location`
  minDate!: NgbDateStruct
  orderProducts: any[] = []
  organization = new Organization()
  organizationLanguages: string[] = []
  productsAll: Product[] = []
  productUnits = productUnits
  sortBy = 'rating'
  
  employeeForms: { [key: number]: FormGroup } = {}

  private _sortedEmployees: EmployeeAvailable[] = []
  
  get sortedEmployees(): EmployeeAvailable[] {
    return this._sortedEmployees
  }

  shiftForm = new FormGroup({
    date: new FormControl<null | string>(null, [Validators.required]),
    start_times: new FormControl<null | string[]>(null),
    assignment_id: new FormControl<null | number>(null, [Validators.required]),
    customer: new FormControl<null | Customer>(null, [Validators.required]),
    employee_id: new FormControl<null | number>(null),
    products: new FormControl<any>([]),
    notes: new FormControl('', { nonNullable: true }),
    discount_id: new FormControl<null | number>(null),
  })

  constructor(
    private settingsService: SettingsService,
    private route: ActivatedRoute,
    public translateField: TranslateFieldPipe,
    public router: Router,
    private modalService: NgbModal,
    private customerService: CustomerService,
    private shiftService: ShiftService,
    private cdRef: ChangeDetectorRef,
    private authService: AuthService,
    private titleService: Title
  ) { }

  ngOnInit() {
    window.scrollTo(0, 0)
    
    this.titleService.setTitle($localize`Schedule`)

    if (localStorage.getItem('order')) {
      const order = JSON.parse(localStorage.getItem('order')!)

      this.shiftForm.patchValue(order)

      this.setLocationName()

      if(!this.shiftForm.controls.assignment_id.value) {
        alert($localize`Your service category is not set. You need to select a category and order services first.`)
        this.router.navigate(['/'])
        return
      }
    }

    else {
      alert($localize`Your order is empty. You need to select a category and order services first.`)
      this.router.navigate(['/'])
    }

    const minDate = new Date()

    // If it's afer 12:00, set min date to tomorrow
    if(minDate.getHours() >= 12) {
      minDate.setDate(minDate.getDate() + 1)
    }

    this.minDate = { year: minDate.getUTCFullYear(), month: minDate.getMonth() + 1, day: minDate.getDate() }

    this.settingsService.assignmentPublic(this.shiftForm.controls.assignment_id.value as number).subscribe(assignment => {
      this.assignment = assignment

      this.settingsService.assignmentProductsPublic(this.assignment.id).subscribe(products => {
        this.productsAll = products
        this.updateOrderProducts()

        // If date is not set today or tomorrow
        if(!this.shiftForm.controls.date.value) {
          this.shiftForm.controls.date.setValue(minDate.getFullYear() + '-' + ('0' + (minDate.getMonth() + 1)).slice(-2) + '-' + ('0' + minDate.getDate()).slice(-2))
        }

        // If everything is set ets list the employees right away
        if(this.shiftForm.controls.customer.value && this.shiftForm.controls.date.value && this.shiftForm.controls.assignment_id.value) {
          this.listEmployees()
        }
      })
    })

    this.settingsService.organizationPublic().subscribe(organization => {
      this.organization = organization
      this.organizationLanguages = [...organization.languages.split(','), 'en']
    })

    this.settingsService.badges().subscribe(badges => {
      this.badges = badges
    })

    this.settingsService.holidaysPublic().subscribe(holidays => {
      this.holidays = holidays
    })

    this.authService.isSignedIn().subscribe(result => {
      this.isSignedIn = result.signed_in

      if (this.isSignedIn) {
        this.customerService.current().subscribe(user => {
          this.currentUser = user

          if(this.currentUser.default_customer_id && !this.shiftForm.controls.customer.value) {
            this.settingsService.customer(this.currentUser.default_customer_id).subscribe(customer => { 
              this.shiftForm.controls.customer.setValue(customer)

              this.setLocationName()
            })
          }

          if (this.currentUser.discounts.length > 0) {
            let order = {}

            if (localStorage.getItem('order')) {
              const order = JSON.parse(localStorage.getItem('order')!)

              // Product controls are maybe not created yet
              //delete (order.products)

              this.shiftForm.patchValue(order)
            }

            this.shiftForm.controls.discount_id.setValue(this.currentUser.discounts[0].id)

            localStorage.setItem('order', JSON.stringify(this.shiftForm.value))
          }
        })
      }
    })
  }

  deleteProduct(index: number) {
    if (!confirm($localize`Are you sure you want to remove this item from your order?`)) {
      return
    }
    this.shiftForm.controls.products.value.splice(index, 1)

    if(this.shiftForm.controls.products.value.length == 0) {
      this.shiftForm.controls.assignment_id.setValue(null)

      alert($localize`Your order is empty. You need to select a category and order services first.`)
      this.router.navigate(['/'])
    }

    this.updateOrderProducts()
    this.listEmployees()
  }

  editProduct(index: number) {
    const modal = this.modalService.open(ProductComponent, { centered: true, fullscreen: 'lg' })

    modal.componentInstance.assignment = this.assignment
    modal.componentInstance.product = this.productsAll.find(p => p.id === this.orderProducts[index].product.id)
    modal.componentInstance.values = this.orderProducts[index]
    modal.componentInstance.noNext = true

    modal.result.then((result) => {
      if (result) {
        if (!this.shiftForm.controls.products.value) {
          this.shiftForm.controls.products.setValue([])
        }

        if (result.number < 1) {
          this.shiftForm.controls.products.value.splice(index, 1)
        }

        else {
          this.shiftForm.controls.products.value[index] = result
        }

        this.updateOrderProducts()
        this.listEmployees()
      }
    })
  }

  updateOrderProducts() {
    this.orderProducts = []

    if (!this.shiftForm.controls.products.value || !Array.isArray(this.shiftForm.controls.products.value)) {
      this.shiftForm.controls.products.setValue([])
    }
    
    for (let product of this.shiftForm.controls.products.value) {
      let productObject = this.productsAll.find(p => p.id == product.product_id)

      this.orderProducts.push({ ...product, product: productObject })
    }

    localStorage.setItem('order', JSON.stringify(this.shiftForm.value))
  }

  setLocation() {
    const modal = this.modalService.open(LocationComponent, { centered: true, size: 'md' })

    modal.result.then((customer) => {
      if (customer) {
        this.shiftForm.controls.customer.setValue(customer)
        this.setLocationName()
        
        if(customer.type != 'individual') {
          this.customerService.vatStatus(customer).subscribe(status => {
            (this.shiftForm.controls.customer.value as Customer).vat_valid = status.valid

            localStorage.setItem('order', JSON.stringify(this.shiftForm.value))

            const ctrls = this.shiftForm.controls

            if (ctrls.customer.value && ctrls.customer.value.area_id && ctrls.date.value) {
              this.listEmployees()
            }
          })
        }

        else {
          const ctrls = this.shiftForm.controls
          
          if (ctrls.customer.value && ctrls.customer.value.area_id && ctrls.date.value) {
            this.listEmployees()
          }
        }
      }
    })
  }

  setLocationName() {
    const customer = this.shiftForm.controls.customer.value

    if (customer) {
      this.locationName = `${customer.street_address}`

      if (customer.office && customer.office != 'undefined')
        this.locationName += `, ${customer.office}`

      this.locationName += `, ${customer.city}`
    }

    else {
      this.locationName = $localize`Set location`
    }
  }

  listEmployees() {
    if (this.shiftForm.valid) {
      this.shiftService.employees(this.shiftForm.value).subscribe(employees => {
        this.firstSearchDone = true

        //this.shiftForm.controls.employee_id.setValue(null)
        this.shiftForm.controls.start_times.setValue(null)

        // Create employee forms
        this.employeeForms = employees.reduce((prev: any, curr: any) => {
          //console.log(curr.availability)
          prev[curr.id] = new FormGroup({
            employee_id: new FormControl<null | number>(curr.id, [Validators.required]),
            start_times: new FormArray(
              curr.availability.map((availability: any) => {
                return new FormControl<null | string>(availability.times[0][0])
              })
            )
          })

          return prev
        }, {})

        this.employees = this.shiftForm.controls.employee_id.value ? employees.filter((employee: any) => employee.id == this.shiftForm.controls.employee_id.value) : employees

        // Update sorted employees when employees array changes
        this.updateSortedEmployees()

        if (this.languageSelect && this.languageSelect.nativeElement) {
          this.languageSelect.nativeElement.focus()
          this.languageSelect.nativeElement.blur()
        }

        this.cdRef.detectChanges()
      })
    }

    else {
      console.log('form not valid (public component)', this.shiftForm.value, this.shiftForm)
      this.employees = []
      this.updateSortedEmployees()
    }
  }

  setBadge(event: any) {
    this.badge = event.target.value
    this.updateSortedEmployees()
  }

  setLanguage(event: any) {
    this.language = event.target.value.split('-')[0]
    this.updateSortedEmployees()
  }

  setSort(event: any) {
    this.sortBy = event.target.value
    this.updateSortedEmployees()
  }

  // Add trackBy function to prevent unnecessary DOM recreation
  trackByEmployee(index: number, employee: EmployeeAvailable): any {
    return employee.id;
  }

  // New method to update sorted employees only when needed
  private updateSortedEmployees(): void {
    let employees = [...this.employees]; // Shallow copy

    if (this.language) {
      employees = employees.filter(employee =>
        employee.skills && employee.skills.find((skill: Skill) => skill.language_code == this.language)
      );
    }

    if (this.badge != '') {
      employees = employees.filter(employee =>
        employee.badges && employee.badges.find((badge: Badge) => badge.id == Number(this.badge))
      );
    }

    if (this.sortBy == 'price_asc') {
      employees.sort((a, b) => a.price - b.price);
    } else if (this.sortBy == 'price_desc') {
      employees.sort((a, b) => b.price - a.price);
    } else if (this.sortBy == 'rating') {
      employees.sort((a, b) => b.rating - a.rating);
    }

    this._sortedEmployees = employees;
  }

  invalidStartDate = (date: NgbDateStruct, current?: { year: number; month: number; }): boolean => {
    const d = new Date(Date.UTC(date.year, date.month - 1, date.day, 0, 0, 0, 0));
    const dateStr = d.toISOString().split('T')[0]

    if (!this.assignment.order_any_day && this.holidays.filter(holiday => holiday.date == dateStr).length > 0) {
      return true
    }

    if (!this.assignment.order_any_day && (
      (d.getDay() == 1 && !this.organization.customer_order_monday)
      || (d.getDay() == 2 && !this.organization.customer_order_tuesday)
      || (d.getDay() == 3 && !this.organization.customer_order_wednesday)
      || (d.getDay() == 4 && !this.organization.customer_order_thursday)
      || (d.getDay() == 5 && !this.organization.customer_order_friday)
      || (d.getDay() == 6 && !this.organization.customer_order_saturday)
      || (d.getDay() == 0 && !this.organization.customer_order_sunday))
    ) {
      return true
    }

    return false
  }

  languageFlag(lang: string) {
    if (lang == 'ar') {
      return '🇪🇬'
    } else if (lang == 'bg') {
      return '🇧🇬'
    } else if (lang == 'bn') {
      return '🇧🇩'
    } else if (lang == 'bs') {
      return '🇧🇦'
    } else if (lang == 'cs') {
      return '🇨🇿'
    } else if (lang == 'da') {
      return '🇩🇰'
    } else if (lang == 'de') {
      return '🇩🇪'
    } else if (lang == 'el') {
      return '🇬🇷'
    } else if (lang == 'en') {
      return '🇬🇧'
    } else if (lang == 'es') {
      return '🇪🇸'
    } else if (lang == 'et') {
      return '🇪🇪'
    } else if (lang == 'fi') {
      return '🇫🇮'
    } else if (lang == 'fr') {
      return '🇫🇷'
    } else if (lang == 'ga') {
      return '🇮🇪'
    } else if (lang == 'ha') {
      return '🇳🇬'
    } else if (lang == 'he') {
      return '🇮🇱'
    } else if (lang == 'hi') {
      return '🇮🇳'
    } else if (lang == 'hr') {
      return '🇭🇷'
    } else if (lang == 'hu') {
      return '🇭🇺'
    } else if (lang == 'hy') {
      return '🇦🇲'
    } else if (lang == 'id') {
      return '🇮🇩'
    } else if (lang == 'it') {
      return '🇮🇹'
    } else if (lang == 'ja') {
      return '🇯🇵'
    } else if (lang == 'ka') {
      return '🇬🇪'
    } else if (lang == 'ko') {
      return '🇰🇷'
    } else if (lang == 'lb') {
      return '🇱🇺'
    } else if (lang == 'lt') {
      return '🇱🇹'
    } else if (lang == 'lv') {
      return '🇱🇻'
    } else if (lang == 'mk') {
      return '🇲🇰'
    } else if (lang == 'mt') {
      return '🇲🇹'
    } else if (lang == 'nl') {
      return '🇳🇱'
    } else if (lang == 'no') {
      return '🇳🇴'
    } else if (lang == 'pl') {
      return '🇵🇱'
    } else if (lang == 'pt') {
      return '🇵🇹'
    } else if (lang == 'ro') {
      return '🇷🇴'
    } else if (lang == 'ru') {
      return '🇷🇺'
    } else if (lang == 'sk') {
      return '🇸🇰'
    } else if (lang == 'sl') {
      return '🇸🇮'
    } else if (lang == 'sq') {
      return '🇦🇱'
    } else if (lang == 'sr') {
      return '🇷🇸'
    } else if (lang == 'sv') {
      return '🇸🇪'
    } else if (lang == 'sw') {
      return '🇰🇪'
    } else if (lang == 'th') {
      return '🇹🇭'
    } else if (lang == 'tl') {
      return '🇵🇭'
    } else if (lang == 'tr') {
      return '🇹🇷'
    } else if (lang == 'uk') {
      return '🇺🇦'
    } else if (lang == 'vi') {
      return '🇻🇳'
    } else if (lang == 'xh') {
      return '🇿🇦'
    } else if (lang == 'yo') {
      return '🇳🇬'
    } else if (lang == 'zh') {
      return '🇨🇳'
    } else if (lang == 'zu') {
      return '🇿🇦'
    } else {
      return '🇺🇳'
    }
  }

  setDate(_event: any) {
    localStorage.setItem('order', JSON.stringify(this.shiftForm.value))

    const ctrl = this.shiftForm.controls

    if (ctrl.customer.value && ctrl.customer.value.area_id && ctrl.date.value) {
      this.listEmployees()
    }
  }

  feedback(employee: Employee) {
    const modal = this.modalService.open(FeedbackComponent, { centered: true, size: 'lg', scrollable: true })
    modal.componentInstance.employee = employee
  }

  selectEmployee(employee_id: number) {
    if(!this.isSignedIn) {
      if(!confirm($localize`You need to sign in/sign up to complete the order.`)) {
        return
      }
    }

    this.shiftForm.controls.employee_id.setValue(employee_id)
    this.shiftForm.controls.start_times.setValue(this.employeeForms[employee_id].controls['start_times'].value)

    this.cdRef.detectChanges()

    localStorage.setItem('order', JSON.stringify(this.shiftForm.value))

    this.router.navigate(['/checkout'])
  }
}
